import { cn } from '@/lib/utils';

interface Dev13LogoProps {
  className?: string;
  /** Override the icon size (default `size-7`). */
  iconClassName?: string;
  /** Override the wordmark type styles (default `text-[1.05rem]`). */
  wordmarkClassName?: string;
  /** Show the wordmark on every breakpoint (default hides it below `sm`). */
  alwaysShowWordmark?: boolean;
}

/**
 * Neutral Dev13Systems platform logo — the orbit mark + wordmark used across the
 * app shell (white-labelled guests), the auth pages and the marketing site.
 * The gradient keeps the product's blue (#3b82f6 → #1d4ed8), self-contained so
 * it renders identically in light and dark surfaces.
 */
export function Dev13Logo({
  className,
  iconClassName,
  wordmarkClassName,
  alwaysShowWordmark = false,
}: Dev13LogoProps) {
  return (
    <span className={cn('flex items-center gap-2.5', className)}>
      <svg
        viewBox="0 0 32 32"
        className={cn('size-7 shrink-0', iconClassName)}
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="dev13logo-grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#3b82f6" />
            <stop offset="1" stopColor="#1d4ed8" />
          </linearGradient>
        </defs>
        <rect x="0.5" y="0.5" width="31" height="31" rx="8" fill="url(#dev13logo-grad)" />
        {/* Faint orbit ring (systems motif) behind the "13". */}
        <circle
          cx="16"
          cy="16"
          r="10.5"
          fill="none"
          stroke="#ffffff"
          strokeOpacity="0.22"
          strokeWidth="1.2"
        />
        <circle cx="16" cy="5.5" r="1.6" fill="#ffffff" fillOpacity="0.85" />
        <text
          x="16"
          y="21.5"
          textAnchor="middle"
          fill="#ffffff"
          fontFamily="ui-sans-serif, system-ui, sans-serif"
          fontSize="15"
          fontWeight="800"
          letterSpacing="-0.5"
        >
          13
        </text>
      </svg>
      <span
        className={cn(
          'text-[1.05rem] font-semibold tracking-tight',
          alwaysShowWordmark ? 'inline' : 'hidden sm:inline',
          wordmarkClassName,
        )}
      >
        Dev13<span className="text-blue-500">Systems</span>
      </span>
    </span>
  );
}
